// Theopolis API client + types + streaming helper.
//
// The console talks directly to Theopolis (the chat backend) using the user's
// Keycloak access token as a Bearer credential. Theopolis verifies tokens
// against the same realm as cortex and only checks the issuer (no audience),
// so our `cortex-console` token is accepted.
//
// Base URL comes from `VITE_THEOPOLIS_API` in the workspace-root `.env`. The
// var is mandatory — `apps/console/vite.config.ts` aborts the build if unset.
import { useMemo } from 'react';

import { useAuth } from '../auth/AuthProvider';

// ── Base URL ──────────────────────────────────────────────────────────────
const env = import.meta.env as Record<string, string | undefined>;
const rawBase = env.VITE_THEOPOLIS_API ?? '';
/** Theopolis origin, no trailing slash. e.g. `http://localhost:3003`. */
export const theopolisBaseUrl = rawBase.replace(/\/$/, '');
/** Theopolis API root, no trailing slash. e.g. `http://localhost:3003/api`. */
export const theopolisApiUrl = `${theopolisBaseUrl}/api`;
/**
 * True when VITE_THEOPOLIS_API is set to a non-empty value. Use this to gate
 * queries so a missing env var surfaces as a clear config error rather than a
 * confusing "JSON.parse: unexpected character" (the Vite dev server returns
 * index.html for unmatched paths, which parses as HTML, not JSON).
 */
export const isTheopolisConfigured = Boolean(theopolisBaseUrl);

// ── Types (mirrors apps/theopolis/openapi.json) ───────────────────────────
export type ModelProvider = 'anthropic' | 'openai';
export type ScheduleStatus = 'ACTIVE' | 'PAUSED' | 'DISABLED';
export type ThreadStatus = 'idle' | 'running';
export type MessageRole = 'user' | 'assistant' | 'system' | 'tool';

export interface IAgent {
  id: string;
  /** Legacy field — agents are now globally scoped, so the server omits it. */
  orgId?: string;
  /**
   * Agent classification: which UI surface this agent is intended for.
   * Today: `'refinement'` for /refinement sessions, `'chat'` for the
   * ad-hoc header-bar chat dialog. Open-ended string so new surfaces
   * don't need a console-side type bump.
   */
  type: string;
  name: string;
  description: string | null;
  instructions: string;
  modelProvider: ModelProvider;
  modelName: string;
  memoryConfig: {
    observationalThreshold?: number;
    workingMemorySize?: number;
  } | null;
  toolConfig: {
    sandbox?: boolean;
    builtin?: string[];
    mcpConnectionIds?: string[];
  } | null;
  builtin: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IThread {
  id: string;
  orgId: string;
  agentId: string;
  title: string | null;
  metadata: Record<string, unknown> | null;
  /**
   * Open-ended classification record. Today's well-known keys:
   *   - `type` (e.g. `'refinement'`).
   *   - `scheduledBy` (the cron schedule id that produced this thread).
   */
  tags: { type?: string; scheduledBy?: string; [key: string]: unknown };
  archived: boolean;
  status: ThreadStatus;
  createdAt: string;
  updatedAt: string;
}

export interface IMessage {
  id: string;
  threadId: string;
  role: MessageRole;
  content: unknown;
  usage: Record<string, unknown> | null;
  createdAt: string;
}

// ── Thread transcript (messages + tool invocations, chronologically merged) ──
// Mirrors `TranscriptItemSchema` in apps/theopolis/src/contracts/message.schema.ts.
// `GET threads/:id/messages/transcript` is the only read-back that carries
// persisted tool args/results — the plain messages endpoint has no tool data.

export interface ITranscriptMessage {
  kind: 'message';
  id: string;
  threadId: string;
  role: MessageRole;
  content: unknown;
  usage: Record<string, unknown> | null;
  createdAt: string;
}

export interface ITranscriptToolInvocation {
  kind: 'tool_invocation';
  id: string;
  threadId: string;
  /** The assistant message this invocation belongs to. */
  messageId: string;
  toolName: string;
  args: unknown;
  result: unknown | null;
  error: string | null;
  durationMs: number;
  createdAt: string;
}

export type TranscriptItem = ITranscriptMessage | ITranscriptToolInvocation;

export interface ISchedule {
  id: string;
  orgId: string;
  agentId: string;
  name: string;
  prompt: string;
  cronExpression: string;
  timezone: string | null;
  titleTemplate: string | null;
  metadata: Record<string, unknown> | null;
  status: ScheduleStatus;
  nextRunAt: string | null;
  lastFiredAt: string | null;
  lastError: string | null;
  consecutiveFailures: number;
  maxConsecutiveFailures: number;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateAgentBody {
  name: string;
  description?: string;
  instructions: string;
  modelProvider: ModelProvider;
  modelName: string;
  memoryConfig?: Record<string, unknown>;
  toolConfig?: { sandbox?: boolean; mcpConnectionIds?: string[] };
}

export interface ICreateThreadBody {
  agentId: string;
  title?: string | null;
  metadata?: Record<string, unknown> | null;
  /** Open-ended classification record. Refinement always sets `{ type: 'refinement' }`. */
  tags?: Record<string, unknown>;
}

export interface ICreateScheduleBody {
  agentId: string;
  name: string;
  prompt: string;
  cronExpression: string;
  timezone?: string | null;
  titleTemplate?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface IUpdateScheduleBody {
  name?: string;
  prompt?: string;
  cronExpression?: string;
  timezone?: string | null;
  titleTemplate?: string | null;
  status?: ScheduleStatus;
  metadata?: Record<string, unknown> | null;
}

// ── Refinement preferences ────────────────────────────────────────────────

/**
 * Pinned eval scope ("advanced mode"). When set on a workflow override, the
 * refinement agent runs evals against exactly this one suite/task and nothing
 * else, optimizing the single `goalAxis` on it (optionally toward the
 * matching target* value); the weight triangle is stored but ignored.
 */
export interface IRefinementEvalScope {
  kind: 'suite' | 'task';
  /** Cortex API Suite/Task id. */
  id: string;
}

export type GoalAxis = 'accuracy' | 'cost' | 'speed';

export interface IRefinementPreference {
  id: string;
  orgId: string;
  /** NULL = org-level default; non-null = per-workflow override. */
  workflowId: string | null;
  /** 0-100 integer percentages; sum to 100 (server-enforced). */
  accuracy: number;
  cost: number;
  speed: number;
  /** Optional absolute targets (null when unset). */
  targetAccuracy: number | null;
  /** USD per run. */
  targetCost: number | null;
  /** Milliseconds. */
  targetSpeed: number | null;
  /** Pinned eval scope; null = no pin. Per-workflow overrides only. */
  evalScope: IRefinementEvalScope | null;
  /** The single advanced-mode objective; null reads as accuracy. */
  goalAxis: GoalAxis | null;
  /** Org-level model override for the refinement agent; null = catalog default (sonnet-4.6). */
  modelProvider: 'anthropic' | 'openai' | null;
  modelName: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Effective preference for a workflow after override → org → default fallback. */
export interface IResolvedRefinementPreference extends IRefinementPreference {
  source: 'workflow' | 'org' | 'default';
}

export interface IUpsertRefinementPreferenceBody {
  /** Omit/null to upsert the org default; set to upsert a workflow override. */
  workflowId?: string | null;
  accuracy: number;
  cost: number;
  speed: number;
  targetAccuracy?: number | null;
  targetCost?: number | null;
  targetSpeed?: number | null;
  /** Requires `workflowId` (server-enforced). Omit or null to clear the pin. */
  evalScope?: IRefinementEvalScope | null;
  /** Requires `evalScope` (server-enforced). */
  goalAxis?: GoalAxis | null;
  /** Org-level model override. Omit/null both to use the catalog default. */
  modelProvider?: 'anthropic' | 'openai' | null;
  modelName?: string | null;
}

// ── Optimization spend limit ──────────────────────────────────────────────
export interface IOptimizationSpendLimit {
  id: string;
  orgId: string;
  /** Monthly USD cap. null = no limit. */
  monthlyLimitUsd: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface IUpsertOptimizationSpendLimitBody {
  /** Monthly USD cap. null (or omit) for no limit. */
  monthlyLimitUsd?: number | null;
}

/** Aggregated session (chat / refinement conversation) cost for the org.
 *  Source of truth: apps/theopolis contracts/cost.schema.ts. */
export interface IMonthlySessionCost {
  orgId: string;
  /** Sum of every session cost_record this calendar month, in USD. */
  totalCostUsd: number;
  /** How many cost records contributed to the total. */
  recordCount: number;
  /** ISO timestamps bounding the window (current calendar month, UTC). */
  periodStart: string;
  periodEnd: string;
}

// ── Org-managed skills ────────────────────────────────────────────────────
// Each row is materialized into a virtual `skills/<name>/SKILL.md` file
// mounted into the chat workspace at agent-build time. An org with no
// rows yields an empty workspace (no built-in defaults).
//
// Skills are versioned: the parent row holds identity (slug `name`) and a
// pointer to the version currently materialized into agents
// (`currentVersionId`). Each content edit cuts a new immutable
// `ISkillVersion` row with an author-supplied `comment` (the "what
// changed" note). Source of truth lives in `apps/theopolis` — see
// contracts/skill.schema.ts.
export interface ISkill {
  id: string;
  orgId: string;
  /** kebab-case slug, unique per org. Renaming is a parent-only edit. */
  name: string;
  /** Single trigger sentence; projection of the current version. */
  description: string;
  /** Raw markdown body; projection of the current version. */
  body: string;
  /** FK to the version agents currently see. */
  currentVersionId: string;
  /** Monotonic version number of the current version (1-indexed). */
  currentVersionNumber: number;
  /** Author's "what changed" note on the current version. */
  currentVersionComment: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * One immutable historical version of a skill.
 *
 *   - `comment` is the author's "what changed" note captured at the time
 *     of the edit (required by the API on any content edit).
 *   - `isCurrent` is computed server-side against the parent's
 *     `currentVersionId` — lets the UI flag the active version without a
 *     second join.
 */
export interface ISkillVersion {
  id: string;
  skillId: string;
  versionNumber: number;
  description: string;
  body: string;
  comment: string;
  createdAt: string;
  /** Keycloak `sub` of the author. `null` for backfilled/system-cut versions. */
  createdBySubject: string | null;
  isCurrent: boolean;
}

export interface ICreateSkillBody {
  name: string;
  description: string;
  body: string;
  /** Optional on create. Server stamps "Initial version" when omitted. */
  comment?: string;
}

export interface IUpdateSkillBody {
  name?: string;
  description?: string;
  body?: string;
  /** REQUIRED when `description` or `body` is set; ignored on rename-only edits. */
  comment?: string;
}

export interface IPromoteSkillVersionBody {
  versionId: string;
}

export interface ITtsBody {
  text: string;
  voice?: 'eve' | 'ara' | 'rex' | 'sal' | 'leo';
  language?: string;
}
export interface ITtsResponse {
  audioUrl: string;
  requestId?: string;
}

// ── Page catalog (apps/theopolis/src/contracts/chat.schema.ts) ────────────
// Mirrors `PageDescriptorSchema` server-side. The console maintains the
// canonical list in `lib/page-catalog.ts` and ships it on every chat send
// from the header-bar Chat surface. The catalog is per-turn ephemeral on
// theopolis — it's never persisted.
export interface IPageDescriptor {
  /** Human-readable label, e.g. "Workflow detail". */
  name: string;
  /** One-sentence description shown to the LLM. */
  description: string;
  /**
   * URL or URL template, e.g. "/workflows" or "/workflows/:id". Theopolis
   * never resolves templates — the LLM picks a concrete URL and the
   * console handles navigation.
   */
  urlTemplate: string;
}

// ── Chat SSE event envelope (apps/theopolis/src/contracts/chat.schema.ts) ──
export type ChatEvent =
  | {
      type: 'start';
      userMessageId: string;
      assistantMessageId: string;
      threadId: string;
    }
  | { type: 'text-delta'; delta: string }
  // Summarized-reasoning token — only present on turns sent with
  // `thinking: true`. Safe to ignore; renderers may use it as a
  // thinking-progress indicator.
  | { type: 'reasoning-delta'; delta: string }
  | { type: 'tool-call'; toolCallId: string; name: string; args: unknown }
  | {
      type: 'tool-result';
      toolCallId: string;
      name: string;
      result: unknown;
      durationMs: number;
    }
  | { type: 'tool-error'; toolCallId: string; message: string }
  | {
      type: 'usage';
      inputTokens: number;
      outputTokens: number;
      costUsd: string;
    }
  | { type: 'finish'; assistantMessageId: string; finishReason: string }
  | { type: 'error'; message: string };

// ── Minimal fetch wrapper (no ky dep) ─────────────────────────────────────
//
// We deliberately avoid pulling in `ky` (used by @cortex/sdk) — the surface
// the console needs is small and a direct fetch wrapper keeps the dependency
// graph tighter for what is, in effect, a separate API target.
//
// API mirrors the subset of `ky` we actually use:
//
//   theo.get(path).json<T>()
//   theo.post(path, { json: body }).json<T>()
//   theo.patch(path, { json: body }).json<T>()
//   theo.delete(path)
//
// Paths are relative to `${VITE_THEOPOLIS_API}/api` and MUST NOT start with `/`.
type JsonInit = { json?: unknown };

interface IResponseLike {
  json: <T>() => Promise<T>;
  text: () => Promise<string>;
  ok: boolean;
  status: number;
}

export interface ITheopolisClient {
  get: (path: string) => IResponseLike;
  post: (path: string, init?: JsonInit) => IResponseLike;
  patch: (path: string, init?: JsonInit) => IResponseLike;
  put: (path: string, init?: JsonInit) => IResponseLike;
  /** Fire-and-await DELETE; returns a real Promise<void> so it slots into useMutation. */
  delete: (path: string) => Promise<void>;
}

function makeClient(token: string | null): ITheopolisClient {
  function call(method: string, path: string, init?: JsonInit): IResponseLike {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (init?.json !== undefined) headers['Content-Type'] = 'application/json';

    // Bail early when VITE_THEOPOLIS_API is unset. Without a real origin the
    // URL collapses to a relative path (/api/…), the Vite dev server returns
    // index.html (200 OK), and the caller gets a confusing JSON.parse error.
    if (!isTheopolisConfigured) {
      const err = Promise.reject(new Error('VITE_THEOPOLIS_API is not set'));
      return {
        json: () => err,
        text: () => err,
        get ok() {
          return false;
        },
        get status() {
          return 0;
        },
      };
    }

    // Lazy: only fire the network on `.json()`/`.text()` so callers can chain.
    const promise = fetch(`${theopolisApiUrl}/${path}`, {
      method,
      headers,
      body: init?.json !== undefined ? JSON.stringify(init.json) : undefined,
    }).then(async (res) => {
      if (!res.ok) {
        let body = '';
        try {
          body = await res.text();
        } catch {
          /* ignore */
        }
        throw new Error(`${method} ${path} → ${res.status}${body ? `: ${body}` : ''}`);
      }
      return res;
    });

    return {
      json: <T>(): Promise<T> => promise.then((r) => r.json() as Promise<T>),
      text: () => promise.then((r) => r.text()),
      get ok() {
        return true;
      },
      get status() {
        return 0;
      },
    };
  }

  return {
    get: (path) => call('GET', path),
    post: (path, init) => call('POST', path, init),
    patch: (path, init) => call('PATCH', path, init),
    put: (path, init) => call('PUT', path, init),
    delete: async (path) => {
      // We don't care about the body; just await for HTTP-level errors.
      await call('DELETE', path).text();
    },
  };
}

/**
 * Returns a Theopolis HTTP client bound to the current Keycloak access token.
 * Memoized on token so Keycloak silent-renew flows hand a fresh JWT to the
 * next call without re-rendering consumers.
 */
export function useTheopolis(): ITheopolisClient {
  const { token } = useAuth();
  return useMemo(() => makeClient(token), [token]);
}

// ── Streaming chat (POST /chat/:threadId/messages → SSE) ──────────────────
/**
 * Result of attempting to start a streaming chat turn.
 * - `ok`: connection live; events arrive via the consumer's callback until done.
 * - `busy`: server returned 409 THREAD_BUSY (a scheduled fire is mid-turn).
 * - `error`: any other failure (network, non-2xx, parse error).
 */
export interface IChatStreamResult {
  status: 'ok' | 'busy' | 'error';
  message?: string;
}

export interface IChatStreamOptions {
  threadId: string;
  content: string;
  token: string | null;
  signal?: AbortSignal;
  onEvent: (event: ChatEvent) => void;
  /**
   * Optional list of pages the LLM is allowed to navigate the user to.
   * When supplied (and the agent has `toolConfig.navigation === true`,
   * i.e. the chat-typed agent), theopolis exposes `list_website_pages`
   * and `navigate_to_page` to the LLM. The console listens for the
   * resulting `tool-call` event and runs its router; theopolis itself
   * never persists or interprets the catalog.
   */
  pageCatalog?: IPageDescriptor[];
  /**
   * Per-turn extended-thinking toggle. true → adaptive thinking with
   * summarized reasoning streamed back as `reasoning-delta` events;
   * false → thinking explicitly disabled (fast mode). Omit to accept the
   * provider/model default — the chat overlay always sends an explicit
   * value so the UI toggle, not the model default, decides.
   */
  thinking?: boolean;
}

/**
 * Open a streaming chat turn against Theopolis. Uses `fetch` + `ReadableStream`
 * (NOT `EventSource` — we need to send the Authorization header).
 *
 * Frame format on the wire (per `chat.controller.ts` + `encoder.ts`):
 *
 *   event: <type>\ndata: <json>\n\n
 *
 * We ignore the `event:` line and route purely off the `type` field of the
 * decoded JSON, matching the existing Theopolis vanilla-JS frontend.
 */
export async function streamChatTurn(opts: IChatStreamOptions): Promise<IChatStreamResult> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;

  let res: Response;
  try {
    const body: { content: string; pageCatalog?: IPageDescriptor[]; thinking?: boolean } = {
      content: opts.content,
    };
    if (opts.pageCatalog && opts.pageCatalog.length > 0) {
      body.pageCatalog = opts.pageCatalog;
    }
    if (opts.thinking !== undefined) {
      body.thinking = opts.thinking;
    }
    res = await fetch(`${theopolisApiUrl}/chat/${encodeURIComponent(opts.threadId)}/messages`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: opts.signal,
    });
  } catch (err) {
    return { status: 'error', message: (err as Error).message };
  }

  if (res.status === 409) {
    let msg = 'Thread is currently busy.';
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) msg = body.message;
    } catch {
      /* ignore */
    }
    return { status: 'busy', message: msg };
  }

  if (!res.ok || !res.body) {
    let body = '';
    try {
      body = await res.text();
    } catch {
      /* ignore */
    }
    return { status: 'error', message: `${res.status} ${body}`.trim() };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx = buffer.indexOf('\n\n');
      while (idx !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const event = parseSseFrame(frame);
        if (event) opts.onEvent(event);
        idx = buffer.indexOf('\n\n');
      }
    }
    return { status: 'ok' };
  } catch (err) {
    return { status: 'error', message: (err as Error).message };
  }
}

function parseSseFrame(frame: string): ChatEvent | null {
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('data: ')) dataLines.push(line.slice(6));
    else if (line.startsWith('data:')) dataLines.push(line.slice(5));
  }
  if (dataLines.length === 0) return null;
  try {
    return JSON.parse(dataLines.join('\n')) as ChatEvent;
  } catch {
    return null;
  }
}

// ── Voice: raw-body POST to /transcribe ───────────────────────────────────
/**
 * Theopolis's transcribe endpoint expects the audio bytes as the **raw**
 * request body (not multipart). Cap is 25 MB server-side. Returns `{text}`.
 */
export async function transcribeAudio(
  blob: Blob,
  contentType: string,
  token: string | null,
): Promise<string> {
  const headers: Record<string, string> = { 'Content-Type': contentType };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${theopolisApiUrl}/transcribe`, {
    method: 'POST',
    headers,
    body: blob,
  });
  if (!res.ok) {
    let body = '';
    try {
      body = await res.text();
    } catch {
      /* ignore */
    }
    throw new Error(`transcribe failed: ${res.status} ${body}`.trim());
  }
  const json = (await res.json()) as { text: string };
  return json.text;
}

// ── Voice: TTS synthesis via /tts ─────────────────────────────────────────
/**
 * Synthesize an audio URL for the given text. Used both by the per-bubble
 * "Read aloud" button and by the chat overlay's auto-read-aloud toggle.
 * Returns the hosted audio URL; callers wrap it in `new Audio(url)` to play.
 */
export async function synthesizeSpeech(
  text: string,
  token: string | null,
  voice: NonNullable<ITtsBody['voice']> = 'leo',
): Promise<string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const body: ITtsBody = { text, voice };
  const res = await fetch(`${theopolisApiUrl}/tts`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`tts failed: ${res.status}`);
  const json = (await res.json()) as ITtsResponse;
  return json.audioUrl;
}
